import * as React from 'react';
import { observer, Observer } from 'mobx-react-lite';
import { Table, Tooltip, Space, Button, Popconfirm } from 'antd';
import {
    StopOutlined,
    DownloadOutlined,
    InfoCircleOutlined,
    CommentOutlined,
    SettingOutlined
} from '@ant-design/icons';
import { ColumnProps } from 'antd/lib/table';
import { SortOrder } from 'antd/lib/table/interface';
import { Utils } from '../../../common/services/Utils';
import { TrainingProjectDashboardStore } from '../../stores';
import { TrainingProjectSessionModel } from '../../models';
import {
    TrainingProjectSessionParametersModal,
    TrainingProjectSessionMetricsModal,
    TrainingProjectSessionLogsModal,
    TrainingSessionStatusTag,
    TrainingProjectSessionNoteModal
} from '..';
import useTableScrollHeight from '../../../common/hooks/useTableScrollHeight';
import './TrainingProjectSessionsList.less';

type ActiveSessionModal = {
    selectedSession: TrainingProjectSessionModel;
    modalType: 'metrics' | 'logs' | 'note' | 'parameters';
};

type Props = {
    store: TrainingProjectDashboardStore;
};

const TrainingProjectSessionsList: React.FC<Props> = ({ store }) => {
    const { scrollHeight } = useTableScrollHeight(280);

    const [activeSessionModal, setActiveSessionModal] = React.useState<ActiveSessionModal | undefined>(undefined);

    const columns: ColumnProps<TrainingProjectSessionModel>[] = [
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (_: unknown, session: TrainingProjectSessionModel) => (
                <Observer>
                    {() => <TrainingSessionStatusTag status={session.status} tooltipText={session.formattedError} />}
                </Observer>
            )
        },
        {
            title: 'Elapsed Time',
            dataIndex: 'elapsedTimeFormatted',
            key: 'elapsedTimeFormatted',
            render: (_: unknown, session: TrainingProjectSessionModel) => (
                <Observer>{() => <span>{session.elapsedTimeFormatted}</span>}</Observer>
            )
        },
        {
            title: 'Metrics',
            dataIndex: 'metrics',
            key: 'metrics',
            ellipsis: true,
            render: (_: unknown, session: TrainingProjectSessionModel) => (
                <Observer>
                    {() => (
                        <a onClick={() => setActiveSessionModal({ selectedSession: session, modalType: 'metrics' })}>
                            {session.serializedMetrics ? session.serializedMetrics : null}
                        </a>
                    )}
                </Observer>
            )
        },
        {
            title: 'Model File Path',
            dataIndex: 'modelFilePath',
            key: 'modelFilePath',
            ellipsis: true,
            render: (_: unknown, session: TrainingProjectSessionModel) => (
                <Observer>
                    {() => (
                        <Tooltip title={session.modelFilePath ?? null} placement="topLeft">
                            <span>{session.modelFilePath ?? null}</span>
                        </Tooltip>
                    )}
                </Observer>
            )
        },
        {
            title: 'Created By',
            dataIndex: 'createdBy',
            key: 'createdBy',
            render: (createdBy: string) => <Observer>{() => <>{store.getUserNameById(createdBy)}</>}</Observer>
        },
        {
            title: 'Last Updated',
            dataIndex: 'updateDate',
            key: 'updateDate',
            sorter: (a: TrainingProjectSessionModel, b: TrainingProjectSessionModel) =>
                Utils.safeDateCompare(a.updateDate || a.createDate, b.updateDate || b.createDate),
            defaultSortOrder: 'descend' as SortOrder,
            render: (_: unknown, session: TrainingProjectSessionModel) => (
                <Observer>
                    {() => (
                        <span>
                            {Utils.formatDateShort(session.updateDate ? session.updateDate : session.createDate)}
                        </span>
                    )}
                </Observer>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, session: TrainingProjectSessionModel) => (
                <Observer>
                    {() => (
                        <div className="row-actions-wrapper" onClick={e => e.stopPropagation()}>
                            <Space size="middle">
                                <Tooltip title={session.sanitizedNotePreview || 'No note'}>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<CommentOutlined />}
                                        onClick={() =>
                                            setActiveSessionModal({ selectedSession: session, modalType: 'note' })
                                        }
                                        style={{ color: session.sanitizedNotePreview ? '#1890ff' : undefined }}
                                        aria-label={
                                            session.sanitizedNotePreview
                                                ? `Notes: ${session.sanitizedNotePreview}`
                                                : 'Notes: No note'
                                        }
                                    />
                                </Tooltip>

                                <Tooltip title="View logs">
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<InfoCircleOutlined />}
                                        onClick={() =>
                                            setActiveSessionModal({ selectedSession: session, modalType: 'logs' })
                                        }
                                    />
                                </Tooltip>

                                <Tooltip
                                    title={
                                        session.parameters
                                            ? 'View parameters'
                                            : 'No parameters captured for this session'
                                    }
                                    placement="topRight"
                                    overlayStyle={{ maxWidth: 400 }}
                                    overlayInnerStyle={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                                >
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<SettingOutlined />}
                                        disabled={!session.parameters}
                                        onClick={() =>
                                            setActiveSessionModal({
                                                selectedSession: session,
                                                modalType: 'parameters'
                                            })
                                        }
                                    />
                                </Tooltip>

                                {session.isCancelable && (
                                    <Popconfirm
                                        title="Are you sure you want to cancel this training session?"
                                        okText="Yes"
                                        cancelText="No"
                                        onConfirm={() => session.cancel()}
                                    >
                                        <Tooltip title="Cancel training session">
                                            <Button type="text" size="small" icon={<StopOutlined />} danger />
                                        </Tooltip>
                                    </Popconfirm>
                                )}

                                {session.modelFilePath && (
                                    <Tooltip title="Download model">
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<DownloadOutlined />}
                                            onClick={() => session.downloadTrainedModel()}
                                        />
                                    </Tooltip>
                                )}
                            </Space>
                        </div>
                    )}
                </Observer>
            )
        }
    ];

    return (
        <>
            <TrainingProjectSessionMetricsModal
                session={activeSessionModal?.selectedSession}
                visible={activeSessionModal?.modalType === 'metrics'}
                onClose={() => setActiveSessionModal(undefined)}
            />

            <TrainingProjectSessionLogsModal
                session={activeSessionModal?.selectedSession}
                visible={activeSessionModal?.modalType === 'logs'}
                onClose={() => setActiveSessionModal(undefined)}
            />

            <TrainingProjectSessionNoteModal
                session={activeSessionModal?.selectedSession}
                visible={activeSessionModal?.modalType === 'note'}
                onClose={() => setActiveSessionModal(undefined)}
            />

            <TrainingProjectSessionParametersModal
                session={activeSessionModal?.selectedSession}
                visible={activeSessionModal?.modalType === 'parameters'}
                onClose={() => setActiveSessionModal(undefined)}
            />

            <Table
                data-id="training-project-sessions-table"
                className="alpha-portal-table training-project-sessions-list"
                loading={store.isLoadingSessions}
                dataSource={store.isLoadingSessions ? [] : store.sessions.slice()}
                columns={columns}
                pagination={{ pageSize: 20, hideOnSinglePage: true, showSizeChanger: false }}
                scroll={{ y: scrollHeight }}
                rowKey={(session: TrainingProjectSessionModel) => session.id}
            />
        </>
    );
};

export default observer(TrainingProjectSessionsList);
